import { useEffect, useMemo, useState } from "react";
import { Link, useOutletContext } from "react-router-dom";
import { fetchCasaMapaAdminDashboard } from "../../../api/clientCasamapaAdminApi";
import { useClientAuth } from "../../../auth/ClientAuthProvider";
import { useCasaMapaAdminPortal } from "../../../hooks/useCasaMapaAdminPortal";
import { useLocale, useCurrency } from "../../../intl";
import "../../../styles/partnerPortalDashboard.css";
import "../../../styles/partnerCasaMapaDashboard.css";

function greetingEmoji() {
  const hour = new Date().getHours();
  if (hour < 12) return "☀️";
  if (hour < 18) return "🌤️";
  return "🌙";
}

function firstName(partner) {
  const raw = partner?.firstName || partner?.displayName || partner?.businessName || "Partner";
  return String(raw).split(" ")[0];
}

function formatPriceRange(agency, formatMoney) {
  if (agency?.priceMinCop == null && agency?.priceMaxCop == null) return "—";
  const min = agency.priceMinCop != null ? formatMoney(agency.priceMinCop) : "—";
  const max = agency.priceMaxCop != null ? formatMoney(agency.priceMaxCop) : "—";
  if (min === max) return min;
  return `${min} – ${max}`;
}

function DashboardSkeleton() {
  return (
    <div className="cm-dashboard cm-dashboard--loading" aria-busy="true">
      <div className="partner-skeleton-block partner-skeleton-hero" />
      <div className="partner-kpi-grid">
        {[1, 2, 3, 4, 5, 6].map((id) => (
          <div key={id} className="partner-skeleton-block partner-skeleton-kpi" />
        ))}
      </div>
      <div className="cm-dashboard__skeleton-main">
        <div className="partner-skeleton-block cm-dashboard__skeleton-panel" />
        <div className="partner-skeleton-block cm-dashboard__skeleton-panel" />
      </div>
    </div>
  );
}

function KpiCard({ icon, label, value, hint, tone = "slate" }) {
  return (
    <article className={`partner-kpi-card partner-kpi-card--${tone}`}>
      <span className="partner-kpi-card__icon" aria-hidden="true">
        {icon}
      </span>
      <span className="partner-kpi-card__label">{label}</span>
      <strong className="partner-kpi-card__value">{value ?? "—"}</strong>
      {hint ? <small className="partner-kpi-card__hint">{hint}</small> : null}
    </article>
  );
}

function PipelineBar({ label, value, max, tone = "blue" }) {
  const pct = max > 0 ? Math.round((value / max) * 100) : 0;
  return (
    <div className="cm-dashboard__pipeline-row">
      <div className="cm-dashboard__pipeline-head">
        <span>{label}</span>
        <strong>{value}</strong>
      </div>
      <div className="cm-dashboard__pipeline-track" aria-hidden="true">
        <div
          className={`cm-dashboard__pipeline-fill cm-dashboard__pipeline-fill--${tone}`}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

function AgencyRow({ agency, t, formatMoney, inventoryPath }) {
  const saleCount = agency.saleListingCount ?? 0;
  const rentCount = agency.rentListingCount ?? 0;
  const mixTotal = saleCount + rentCount;
  const salePct = mixTotal > 0 ? Math.round((saleCount / mixTotal) * 100) : 50;

  return (
    <article className="cm-dashboard__agency-row">
      <div className="cm-dashboard__agency-row-head">
        {agency.logoUrl ? (
          <img src={agency.logoUrl} alt="" className="cm-dashboard__agency-logo" />
        ) : (
          <span className="cm-dashboard__agency-mark" aria-hidden="true">
            {String(agency.name || "?").slice(0, 1)}
          </span>
        )}
        <div className="cm-dashboard__agency-copy">
          <h3>{agency.name}</h3>
          <p>
            {(agency.activeListingCount ?? 0).toLocaleString()}{" "}
            {t("realEstate.casaMapa.admin.agencies.activeListings").toLowerCase()}
            {agency.priceMinCop != null || agency.priceMaxCop != null
              ? ` · ${formatPriceRange(agency, formatMoney)}`
              : ""}
          </p>
        </div>
        <div className="cm-dashboard__agency-actions">
          <Link className="cm-dashboard__agency-btn" to={inventoryPath}>
            {t("realEstate.casaMapa.admin.agencies.reviewInventory")}
          </Link>
        </div>
      </div>
      {mixTotal > 0 ? (
        <div className="cm-dashboard__mix" aria-hidden="true">
          <div className="cm-dashboard__mix-labels">
            <span>
              {t("realEstate.casaMapa.admin.listingType.sale")} {salePct}%
            </span>
            <span>
              {t("realEstate.casaMapa.admin.listingType.rent")} {100 - salePct}%
            </span>
          </div>
          <div className="cm-dashboard__mix-track">
            <div className="cm-dashboard__mix-sale" style={{ width: `${salePct}%` }} />
            <div className="cm-dashboard__mix-rent" style={{ width: `${100 - salePct}%` }} />
          </div>
        </div>
      ) : null}
    </article>
  );
}

export default function CasaMapaAdminDashboardPage() {
  const { clientUser } = useClientAuth();
  const { permissions } = useOutletContext() || {};
  const { clientViewId, path, portalLabel, firstName: adminFirstName, viewBase } = useCasaMapaAdminPortal();
  const { t } = useLocale();
  const { formatMoney } = useCurrency();
  const [data, setData] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [loading, setLoading] = useState(true);

  const leadsPath = viewBase ? `${viewBase}/opportunities` : path("dashboard");

  useEffect(() => {
    if (!clientViewId) {
      setLoading(false);
      return;
    }

    let cancelled = false;

    fetchCasaMapaAdminDashboard(clientViewId)
      .then((body) => {
        if (cancelled) return;
        const raw = body?.summary || body;
        setData({
          summary: {
            assignedLeads: raw.operations?.assignedLeads ?? 0,
            newLeads: raw.operations?.newLeads ?? 0,
            contactedLeads: raw.operations?.contactedLeads ?? 0,
            closedLeads: raw.operations?.closedLeads ?? 0,
            correctionsPending: raw.operations?.correctionsPending ?? 0,
            profileCompleteness: 100,
          },
          casamapaInventory: {
            agencyCount: raw.agencyCount ?? 0,
            totalListings: raw.totalListings ?? 0,
            saleListings: raw.saleListings ?? 0,
            rentListings: raw.rentListings ?? 0,
            agencies: raw.topAgencies || [],
            topAgencies: raw.topAgencies || [],
            vendorDirectory: raw.vendorDirectory,
          },
        });
      })
      .catch((error) => {
        if (!cancelled) {
          setErrorMessage(error.message || t("realEstate.casaMapa.admin.errors.loadDashboard"));
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [clientViewId, t]);

  const summary = data?.summary;
  const inventory = data?.casamapaInventory;
  const vendors = inventory?.vendorDirectory;
  const agencies = inventory?.agencies || inventory?.topAgencies || [];

  const attentionItems = useMemo(() => {
    if (!summary) return [];
    const items = [];

    if (summary.newLeads > 0) {
      items.push({
        label: t("realEstate.casaMapa.admin.partnerDashboard.attention.newLeads", {
          count: summary.newLeads,
        }),
        to: leadsPath,
        action: t("realEstate.casaMapa.admin.partnerDashboard.actions.review"),
        tone: "urgent",
      });
    }

    if (summary.correctionsPending > 0) {
      items.push({
        label: t("realEstate.casaMapa.admin.partnerDashboard.attention.corrections", {
          count: summary.correctionsPending,
        }),
        to: path("listing-corrections"),
        action: t("realEstate.casaMapa.admin.partnerDashboard.actions.open"),
        tone: "warn",
      });
    }

    if ((vendors?.pendingCandidates ?? 0) > 0) {
      items.push({
        label: t("realEstate.casaMapa.admin.dashboard.pendingCandidatesAttention", {
          count: vendors.pendingCandidates,
        }),
        to: path("vendors"),
        action: t("realEstate.casaMapa.admin.dashboard.reviewVendors"),
        tone: "warn",
      });
    }

    if ((vendors?.draftVendors ?? 0) > 0) {
      items.push({
        label: t("realEstate.casaMapa.admin.dashboard.draftVendorsAttention", {
          count: vendors.draftVendors,
        }),
        to: path("vendors"),
        action: t("realEstate.casaMapa.admin.dashboard.reviewVendors"),
        tone: "neutral",
      });
    }

    if (summary.profileCompleteness < 100) {
      items.push({
        label: t("realEstate.casaMapa.admin.partnerDashboard.attention.profile"),
        to: path("profile"),
        action: t("realEstate.casaMapa.admin.partnerDashboard.actions.finish"),
        tone: "neutral",
      });
    }

    if (!agencies.length) {
      items.push({
        label: t("realEstate.casaMapa.admin.partnerDashboard.attention.noAgencies"),
        to: path("agencies"),
        action: t("realEstate.casaMapa.admin.partnerDashboard.actions.browse"),
        tone: "neutral",
      });
    }

    return items;
  }, [agencies.length, leadsPath, path, summary, t, vendors?.draftVendors, vendors?.pendingCandidates]);

  const salePct = useMemo(() => {
    const total = (inventory?.saleListings ?? 0) + (inventory?.rentListings ?? 0);
    if (!total) return 50;
    return Math.round(((inventory?.saleListings ?? 0) / total) * 100);
  }, [inventory?.rentListings, inventory?.saleListings]);

  if (loading) {
    return <DashboardSkeleton />;
  }

  if (errorMessage) {
    return (
      <div className="cm-dashboard">
        <p className="admin-data-health-error">{errorMessage}</p>
      </div>
    );
  }

  if (!clientViewId) {
    return <p className="muted">Select a CasaMapa client view to continue.</p>;
  }

  if (!summary) {
    return null;
  }

  const leadMax = Math.max(summary.assignedLeads, summary.newLeads, 1);
  const operatorName =
    clientUser?.displayName || clientUser?.firstName || clientUser?.email || adminFirstName;

  return (
    <div className="cm-dashboard">
      <header className="cm-dashboard__hero partner-dashboard-hero">
        <div>
          <p className="partner-dashboard-hero__eyebrow">
            {greetingEmoji()} {portalLabel}
          </p>
          <h2>
            {t("realEstate.casaMapa.admin.partnerDashboard.welcome", {
              name: operatorName.split(" ")[0],
            })}
          </h2>
          <p className="partner-dashboard-hero__intro">
            {t("realEstate.casaMapa.admin.dashboard.subtitle")}
          </p>
        </div>
        <div className="partner-dashboard-hero__actions">
          <Link className="partner-dashboard-btn partner-dashboard-btn--primary" to={path("agencies")}>
            🏢 {t("realEstate.casaMapa.admin.nav.agencies")}
          </Link>
          <Link className="partner-dashboard-btn" to={path("vendors")}>
            🛠️ {t("realEstate.casaMapa.admin.nav.vendors")}
          </Link>
          <Link className="partner-dashboard-btn" to={leadsPath}>
            📬 {t("realEstate.casaMapa.admin.nav.leads")}
          </Link>
        </div>
      </header>

      <section className="partner-quick-grid" aria-label={t("realEstate.casaMapa.admin.dashboard.quickActions")}>
        <Link className="partner-quick-card" to={path("agencies")}>
          <span className="partner-quick-card__emoji">🏢</span>
          <strong>{t("realEstate.casaMapa.admin.nav.agencies")}</strong>
          <span>
            {inventory
              ? t("realEstate.casaMapa.admin.dashboard.agencyQuickStat", {
                  count: inventory.agencyCount,
                  listings: (inventory.totalListings ?? 0).toLocaleString(),
                })
              : t("realEstate.casaMapa.admin.dashboard.agenciesHint")}
          </span>
        </Link>
        <Link className="partner-quick-card" to={path("listings")}>
          <span className="partner-quick-card__emoji">🏘️</span>
          <strong>{t("realEstate.casaMapa.admin.nav.listings")}</strong>
          <span>
            {inventory
              ? t("realEstate.casaMapa.admin.dashboard.listingsQuickStat", {
                  sale: inventory.saleListings ?? 0,
                  rent: inventory.rentListings ?? 0,
                })
              : t("realEstate.casaMapa.admin.dashboard.listingsHint")}
          </span>
        </Link>
        <Link className="partner-quick-card" to={path("vendors")}>
          <span className="partner-quick-card__emoji">🛠️</span>
          <strong>{t("realEstate.casaMapa.admin.nav.vendors")}</strong>
          <span>
            {vendors
              ? t("realEstate.casaMapa.admin.dashboard.vendorsQuickStat", {
                  published: vendors.publishedVendors ?? 0,
                  draft: vendors.draftVendors ?? 0,
                })
              : t("realEstate.casaMapa.admin.dashboard.vendorsHint")}
          </span>
        </Link>
        <Link className="partner-quick-card" to={leadsPath}>
          <span className="partner-quick-card__emoji">📥</span>
          <strong>{t("realEstate.casaMapa.admin.nav.leads")}</strong>
          <span>
            {t("realEstate.casaMapa.admin.dashboard.leadsQuickStat", {
              new: summary.newLeads,
              total: summary.assignedLeads,
            })}
          </span>
        </Link>
        {permissions?.manageIam ? (
          <Link className="partner-quick-card" to={path("iam")}>
            <span className="partner-quick-card__emoji">🔐</span>
            <strong>{t("realEstate.casaMapa.admin.nav.access")}</strong>
            <span>{t("realEstate.casaMapa.admin.dashboard.accessHint")}</span>
          </Link>
        ) : null}
      </section>

      <section className="partner-kpi-grid" aria-label={t("realEstate.casaMapa.admin.dashboard.kpiLabel")}>
        <KpiCard
          icon="🏢"
          label={t("realEstate.casaMapa.admin.dashboard.agencyCount")}
          value={inventory?.agencyCount ?? 0}
          hint={t("realEstate.casaMapa.admin.dashboard.agenciesHint")}
          tone="slate"
        />
        <KpiCard
          icon="🏠"
          label={t("realEstate.casaMapa.admin.dashboard.totalListings")}
          value={(inventory?.totalListings ?? 0).toLocaleString()}
          hint={
            inventory
              ? `${inventory.saleListings ?? 0} ${t("realEstate.casaMapa.admin.listingType.sale").toLowerCase()} · ${inventory.rentListings ?? 0} ${t("realEstate.casaMapa.admin.listingType.rent").toLowerCase()}`
              : undefined
          }
          tone="blue"
        />
        <KpiCard
          icon="✅"
          label={t("realEstate.casaMapa.admin.dashboard.publishedVendors")}
          value={vendors?.publishedVendors ?? 0}
          hint={t("realEstate.casaMapa.admin.dashboard.vendorDraftHint", {
            count: vendors?.draftVendors ?? 0,
          })}
          tone="green"
        />
        <KpiCard
          icon="📥"
          label={t("realEstate.casaMapa.admin.dashboard.pendingCandidates")}
          value={vendors?.pendingCandidates ?? 0}
          hint={t("realEstate.casaMapa.admin.dashboard.vendorsHint")}
          tone="amber"
        />
        <KpiCard
          icon="🆕"
          label={t("realEstate.casaMapa.admin.partnerDashboard.kpi.newLeads")}
          value={summary.newLeads}
          hint={t("realEstate.casaMapa.admin.partnerDashboard.kpi.newLeadsHint")}
          tone="violet"
        />
        <KpiCard
          icon="✏️"
          label={t("realEstate.casaMapa.admin.nav.corrections")}
          value={summary.correctionsPending}
          hint={t("realEstate.casaMapa.admin.dashboard.correctionsHint")}
          tone="amber"
        />
      </section>

      <div className="cm-dashboard__main">
        <div className="cm-dashboard__primary">
          {inventory && (inventory.saleListings > 0 || inventory.rentListings > 0) ? (
            <section className="partner-panel cm-dashboard__snapshot">
              <div className="partner-panel__header">
                <div>
                  <h3>{t("realEstate.casaMapa.admin.dashboard.marketplaceSnapshot")}</h3>
                  <p className="muted">{t("realEstate.casaMapa.admin.dashboard.inventoryMixHint")}</p>
                </div>
              </div>
              <div className="cm-dashboard__snapshot-stats">
                <div>
                  <span className="cm-dashboard__snapshot-label">
                    {t("realEstate.casaMapa.admin.listingType.sale")}
                  </span>
                  <strong>{(inventory.saleListings ?? 0).toLocaleString()}</strong>
                </div>
                <div>
                  <span className="cm-dashboard__snapshot-label">
                    {t("realEstate.casaMapa.admin.listingType.rent")}
                  </span>
                  <strong>{(inventory.rentListings ?? 0).toLocaleString()}</strong>
                </div>
                <div>
                  <span className="cm-dashboard__snapshot-label">
                    {t("realEstate.casaMapa.admin.dashboard.totalListings")}
                  </span>
                  <strong>{(inventory.totalListings ?? 0).toLocaleString()}</strong>
                </div>
              </div>
              <div className="cm-dashboard__mix cm-dashboard__mix--large">
                <div className="cm-dashboard__mix-labels">
                  <span>
                    {t("realEstate.casaMapa.admin.listingType.sale")} {salePct}%
                  </span>
                  <span>
                    {t("realEstate.casaMapa.admin.listingType.rent")} {100 - salePct}%
                  </span>
                </div>
                <div className="cm-dashboard__mix-track">
                  <div className="cm-dashboard__mix-sale" style={{ width: `${salePct}%` }} />
                  <div className="cm-dashboard__mix-rent" style={{ width: `${100 - salePct}%` }} />
                </div>
              </div>
            </section>
          ) : null}

          <section className="partner-panel">
            <div className="partner-panel__header">
              <div>
                <h3>{t("realEstate.casaMapa.admin.dashboard.topAgencies")}</h3>
                <p className="muted">{t("realEstate.casaMapa.admin.dashboard.topAgenciesHint")}</p>
              </div>
              <Link className="partner-dashboard-btn" to={path("agencies")}>
                {t("realEstate.casaMapa.admin.dashboard.viewAllAgencies")}
              </Link>
            </div>

            {agencies.length ? (
              <div className="cm-dashboard__agency-list">
                {agencies.slice(0, 5).map((agency) => (
                  <AgencyRow
                    key={agency.slug}
                    agency={agency}
                    t={t}
                    formatMoney={formatMoney}
                    inventoryPath={path(`agencies/${agency.slug}/inventory`)}
                  />
                ))}
              </div>
            ) : (
              <p className="muted">{t("realEstate.casaMapa.admin.partnerDashboard.noAgencies")}</p>
            )}
          </section>
        </div>

        <aside className="cm-dashboard__sidebar">
          <section className="partner-panel">
            <h3>{t("realEstate.casaMapa.admin.partnerDashboard.leadPipeline")}</h3>
            <PipelineBar
              label={t("realEstate.casaMapa.admin.partnerDashboard.pipeline.new")}
              value={summary.newLeads}
              max={leadMax}
              tone="green"
            />
            <PipelineBar
              label={t("realEstate.casaMapa.admin.partnerDashboard.pipeline.inProgress")}
              value={summary.contactedLeads}
              max={leadMax}
              tone="blue"
            />
            <PipelineBar
              label={t("realEstate.casaMapa.admin.partnerDashboard.pipeline.closed")}
              value={summary.closedLeads}
              max={leadMax}
              tone="slate"
            />
            <Link className="cm-dashboard__panel-link" to={leadsPath}>
              {t("realEstate.casaMapa.admin.partnerDashboard.viewLeads")} →
            </Link>
          </section>

          <section className="partner-panel">
            <div className="partner-panel__header">
              <h3>{t("realEstate.casaMapa.admin.partnerDashboard.needsAttention")}</h3>
            </div>
            {attentionItems.length ? (
              <ul className="cm-dashboard__attention">
                {attentionItems.map((item) => (
                  <li key={item.label} className={`cm-dashboard__attention-item cm-dashboard__attention-item--${item.tone}`}>
                    <span>{item.label}</span>
                    <Link to={item.to}>{item.action}</Link>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="muted">{t("realEstate.casaMapa.admin.partnerDashboard.allCaughtUp")}</p>
            )}
          </section>

          {vendors ? (
            <section className="partner-panel cm-dashboard__vendor-panel">
              <div className="partner-panel__header">
                <h3>{t("realEstate.casaMapa.admin.dashboard.vendorDirectoryTitle")}</h3>
              </div>
              <dl className="cm-dashboard__vendor-stats">
                <div>
                  <dt>{t("realEstate.casaMapa.admin.dashboard.publishedVendors")}</dt>
                  <dd>{vendors.publishedVendors ?? 0}</dd>
                </div>
                <div>
                  <dt>{t("realEstate.casaMapa.admin.dashboard.draftVendors")}</dt>
                  <dd>{vendors.draftVendors ?? 0}</dd>
                </div>
                <div>
                  <dt>{t("realEstate.casaMapa.admin.dashboard.pendingCandidates")}</dt>
                  <dd>{vendors.pendingCandidates ?? 0}</dd>
                </div>
              </dl>
              <Link className="partner-dashboard-btn partner-dashboard-btn--primary cm-dashboard__vendor-cta" to={path("vendors")}>
                {t("realEstate.casaMapa.admin.dashboard.openVendors")}
              </Link>
            </section>
          ) : null}
        </aside>
      </div>
    </div>
  );
}
